import React from 'react';
import type { RequestItem } from '../../types';

type Props = {
  items: RequestItem[];
  canRemove: boolean;
  canClear: boolean;
  showRemoveButton?: boolean;
  showClearButton?: boolean;
  emptyMessage?: string;
  onRemoveItem: (itemId: string) => void;
  onClear: () => void;
};

function getRequestItemTitle(item: RequestItem) {
  return item.productName ?? item.product?.name ?? 'Untitled request';
}

function getRequestItemSubtitle(item: RequestItem) {
  const parts = [
    item.finish ? `Finish: ${item.finish}` : null,
    item.combination?.color_description ?? null,
  ].filter((entry): entry is string => Boolean(entry));

  if (parts.length > 0) return parts.join(' · ');
  if (item.colours.length > 0) return item.colours.join(', ');
  return 'Awaiting details';
}

export const RequestItemsPanel: React.FC<Props> = React.memo(({
  items,
  canRemove,
  canClear,
  showRemoveButton = canRemove,
  showClearButton = canClear,
  emptyMessage = 'No items selected. Browse the gallery to add products to your request.',
  onRemoveItem,
  onClear,
}) => {
  if (items.length === 0) {
    return (
      <div className="mt-4 rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-4 py-6 text-center text-sm text-stone-500">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-stone-200 bg-white/90 p-3">
      <div className="flex items-center justify-between gap-3 border-b border-stone-200 pb-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-stone-500">
            Request Items
          </div>
          <div className="text-sm font-semibold text-stone-900">
            {items.length} item{items.length === 1 ? '' : 's'}
          </div>
        </div>
        {showClearButton ? (
          <button
            type="button"
            disabled={!canClear}
            onClick={onClear}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
              canClear
                ? 'border border-stone-300 text-stone-700 hover:border-stone-500 hover:bg-stone-100'
                : 'cursor-not-allowed border border-stone-200 bg-stone-100 text-stone-400'
            }`}
          >
            Clear Request
          </button>
        ) : null}
      </div>
      <div className="mt-3 space-y-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start justify-between gap-3 rounded-2xl border border-stone-200 bg-stone-50 px-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-stone-900">
                {getRequestItemTitle(item)}
              </div>
              <div className="mt-0.5 text-xs text-stone-600">
                {getRequestItemSubtitle(item)}
              </div>
              {item.colours.length > 0 ? (
                <div className="mt-1 text-[11px] text-stone-500">
                  Colours: {item.colours.join(', ')}
                </div>
              ) : null}
            </div>
            {showRemoveButton ? (
              <button
                type="button"
                aria-disabled={!canRemove}
                onClick={() => {
                  if (!canRemove) return;
                  onRemoveItem(item.id);
                }}
                aria-label={`Remove ${getRequestItemTitle(item)}`}
                className={`shrink-0 rounded-full px-2 py-1 text-sm font-semibold leading-none transition ${
                  canRemove
                    ? 'cursor-pointer border border-stone-300 text-stone-400 hover:border-red-300 hover:bg-red-50 hover:text-red-600'
                    : 'cursor-pointer border border-stone-200 bg-stone-100 text-stone-300'
                }`}
                title="Remove item"
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
});

export default RequestItemsPanel;
